import { useField } from "formik";
import React, { useState } from "react";
import { Button, BUTTON_COLOR, Modal } from "taltech-styleguide";
import BuildingTypesHeader from "./BuildingTypesHeader";
import { ParameterStructureView } from "./TypesMaterialStructure";
import {
  FeedbackMessage,
  InputNumber,
  InputSelect,
} from "@components-common/forms";
import { StructuralLayer } from "../../materials";
import { IconTalTech } from "@components-common/icons";
import { ActionButton } from "../../../../components-common/buttons/ActionButton";

type IMaterialStructureElementTable = {
  heading: "interior" | "exterior" | "core";
  viewModel: ParameterStructureView;
};

interface Layer {
  location: string;
  materialId: string;
  thickness: number;
  elementId: string;
  functionn:
    | "STRUCTURE"
    | "SUBSTRATE"
    | "THERMAL_AIR"
    | "FINISH1"
    | "FINISH2"
    | "MEMBRANE";
}

const ConfirmationModal = ({
  modalOpen,
  toggleModal,
  layer,
  metaLayer,
  indexToDelete,
  setIndexToDelete,
}: any) => (
  <Modal show={modalOpen} onHide={toggleModal}>
    <Modal.Header closeButton>Confirmation</Modal.Header>
    <Modal.Body>Are you sure you want to Delete?</Modal.Body>
    <Modal.Footer>
      <Button
        variant={BUTTON_COLOR.PRIMARY}
        onClick={() => {
          const newValues = layer.value.filter(
            (element: StructuralLayer, index: number) => {
              return index !== indexToDelete;
            }
          );
          metaLayer.setValue(newValues);
          toggleModal();
          setIndexToDelete(0);
        }}
      >
        Confirm
      </Button>
      <Button variant={BUTTON_COLOR.LINK} onClick={toggleModal}>
        Cancel
      </Button>
    </Modal.Footer>
  </Modal>
);

/**
 * Functional Component: MaterialStructureElementTable
 *
 * This component renders a table for managing material structure elements for a specific heading (e.g., "exterior", "core", "interior").
 * It includes functionality to add new layers, delete layers, and manage layer details such as material and function.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.heading - The heading for the table section (e.g., "exterior", "core", "interior").
 * @param {any} props.viewModel - The view model containing options for materials and functions.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the material structure element table.
 */
export const MaterialStructureElementTable: React.FC<{
  heading: string;
  viewModel: any;
}> = ({ heading, viewModel }) => {
  const { materialOptions, functionOptions } = viewModel;
  const [layer, , metaLayer] = useField(`layers.${heading}`);
  const [modalOpen, setModalOpen] = useState(false);
  const [indexToDelete, setIndexToDelete] = useState(0);
  const toggleModal = () => setModalOpen(!modalOpen);

  const addNewLayer = () => {
    if (materialOptions.length === 0) return;
    const newItem = {
      name: "",
      location: heading.toUpperCase(),
      materialId: materialOptions[0].value,
      thickness: 1,
      elementId: "",
      function: "STRUCTURE",
    };

    metaLayer.setValue([...layer.value, newItem]);
  };

  return (
    <>
      <ConfirmationModal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        metaLayer={metaLayer}
        layer={layer}
        indexToDelete={indexToDelete}
        setIndexToDelete={setIndexToDelete}
      />

      <h6 className={"ebd-material-header"}>{heading}</h6>
      <div className={"table-element"}>
        <BuildingTypesHeader addNewFn={addNewLayer} showAddNew={true} />
        {layer.value.map((parameters: any, index: number) => (
          <>
            <div
              className={"row-element"}
              style={{ border: "none" }}
              key={index}
            >
              <div className={"structure-cell"}>
                <InputSelect
                  id={`layers.${heading}[${index}].materialId`}
                  showLabel={false}
                  options={materialOptions}
                />
              </div>
              <div className={"structure-cell"}>
                <InputSelect
                  id={`layers.${heading}[${index}].function`}
                  showLabel={false}
                  options={functionOptions}
                />
              </div>
              <div className={"structure-cell"}>
                <InputNumber
                  id={`layers.${heading}[${index}].thickness`}
                  elementClass={"ebd-min-input-width"}
                  fixedDecimalScale
                  decimalScale={3}
                  suffix={" m"}
                />
              </div>
              <div
                className={
                  "structure-cell d-flex justify-content-end cursor-pointer"
                }
              >
                <ActionButton
                  kind="delete"
                  onAction={() => {
                    toggleModal();
                    setIndexToDelete(index);
                  }}
                  visual="icon"
                  className="cursor-pointer"
                />
              </div>
            </div>
            <div className={"row-element p-0"} key={`${index}-errors`}>
              <div className={"structure-cell"}>
                <FeedbackMessage
                  fieldId={`layers.${heading}[${index}].materialId`}
                />
              </div>
              <div className={"structure-cell"}>
                <FeedbackMessage
                  fieldId={`layers.${heading}[${index}].function`}
                />
              </div>
              <div className={"structure-cell"}>
                <FeedbackMessage
                  fieldId={`layers.${heading}[${index}].thickness`}
                />
              </div>
              <div
                className={
                  "structure-cell d-flex justify-content-end cursor-pointer"
                }
              ></div>
            </div>
          </>
        ))}
      </div>
    </>
  );
};
